import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel } from "@/components/terminal";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { ShieldCheck, ShieldAlert, Play, FileCheck, AlertTriangle, Building2,
         ClipboardList, ScrollText, KeySquare, Bug, Scale, Database } from "lucide-react";

const STATUS_CLS = {
  PASSING: "text-emerald-400 border-emerald-800", PASS: "text-emerald-400 border-emerald-800",
  COMPLETED: "text-emerald-400 border-emerald-800", EFFECTIVE: "text-emerald-400 border-emerald-800",
  CLOSED: "text-slate-400 border-slate-700", VERIFIED: "text-emerald-400 border-emerald-800",
  FAILING: "text-red-400 border-red-800", FAIL: "text-red-400 border-red-800",
  OPEN: "text-red-400 border-red-800", CRITICAL: "text-red-400 border-red-800",
  DETECTED: "text-red-400 border-red-800",
  REQUIRES_REVIEW: "text-amber-300 border-amber-800", REVIEW_DUE: "text-amber-300 border-amber-800",
  OVERDUE: "text-amber-300 border-amber-800", HIGH: "text-amber-300 border-amber-800",
  ACKNOWLEDGED: "text-amber-300 border-amber-800", TRIAGED: "text-amber-300 border-amber-800",
  CONTAINED: "text-amber-300 border-amber-800", DRAFT: "text-slate-400 border-slate-600",
  MEDIUM: "text-amber-300 border-amber-800", OWNER_APPROVED: "text-sky-300 border-sky-800",
  IN_REMEDIATION: "text-sky-300 border-sky-800", READY_FOR_RETEST: "text-sky-300 border-sky-800",
  REMEDIATED: "text-sky-300 border-sky-800", NOT_TESTED: "text-slate-500 border-slate-700",
  SUPERSEDED: "text-slate-500 border-slate-700", LOW: "text-emerald-400 border-emerald-800",
  MITIGATED: "text-emerald-400 border-emerald-800", ACCEPTED: "text-sky-300 border-sky-800",
};
const Badge = ({ v }) => (
  <span className={`text-[9px] font-mono border rounded-sm px-1.5 py-0.5 ${STATUS_CLS[v] || "text-slate-400 border-slate-700"}`}>{v}</span>
);
const Tile = ({ label, value, testId, tone = "text-slate-50" }) => (
  <div className="bg-[#17191E] border border-[#2D323E] rounded-md p-3" data-testid={testId}>
    <div className="metric-label">{label}</div>
    <div className={`font-mono text-xl font-bold tabular-nums mt-1 ${tone}`}>{value}</div>
  </div>
);

const TABS = [
  ["controls", "Control Registry", ShieldCheck], ["evidence", "Evidence Vault", Database],
  ["remediation", "Remediation", ClipboardList],
  ["policies", "Policy Center", ScrollText], ["access", "Access Reviews", KeySquare],
  ["vendors", "Vendors & Risks", Building2], ["incidents", "Incident Center", Bug],
  ["soc2", "SOC 2 Map", Scale],
];

export default function Assurance() {
  const { user } = useAuth();
  const isOwner = ["owner", "platform_owner", "admin"].includes(user?.role);
  const [posture, setPosture] = useState(null);
  const [controls, setControls] = useState([]);
  const [families, setFamilies] = useState({});
  const [rems, setRems] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [risks, setRisks] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [soc2, setSoc2] = useState(null);
  const [evidence, setEvidence] = useState([]);
  const [famFilter, setFamFilter] = useState("ALL");
  const [evControl, setEvControl] = useState("");
  const [tab, setTab] = useState("controls");
  const [busy, setBusy] = useState("");
  const [incidentForm, setIncidentForm] = useState({ title: "", severity: "MEDIUM", description: "" });

  const load = useCallback(() => {
    api.get("/assurance/posture").then((r) => setPosture(r.data)).catch(() => {});
    api.get("/assurance/controls").then((r) => { setControls(r.data.controls); setFamilies(r.data.families); }).catch(() => {});
    api.get("/assurance/remediations").then((r) => setRems(r.data.remediations)).catch(() => {});
    api.get("/assurance/policies").then((r) => setPolicies(r.data.policies)).catch(() => {});
    api.get("/assurance/access-reviews").then((r) => setReviews(r.data.access_reviews)).catch(() => {});
    api.get("/assurance/vendors").then((r) => setVendors(r.data.vendors)).catch(() => {});
    api.get("/assurance/risks").then((r) => setRisks(r.data.risks)).catch(() => {});
    api.get("/assurance/incidents").then((r) => setIncidents(r.data.incidents)).catch(() => {});
    api.get("/assurance/evidence").then((r) => setEvidence(r.data.evidence)).catch(() => {});
    api.get("/assurance/soc2-map").then((r) => setSoc2(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); toast.success(ok); load(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(""); }
  };
  const transition = (kind, id, to_state, extra = {}) =>
    act(`${kind}-${id}-${to_state}`,
         () => api.post(`/assurance/${kind}/${id}/transition`, { to_state, ...extra }),
         `${id} → ${to_state}`);

  const c = posture?.controls || {};
  const keyControl = (label, ev, testId) => (
    <div className="bg-[#121316] border border-[#222630] rounded-md p-3" data-testid={testId}>
      <div className="metric-label">{label}</div>
      {ev ? (
        <div className="mt-1 flex items-center gap-2">
          <Badge v={ev.result} />
          <span className="text-[10px] font-mono text-slate-500">{String(ev.generated_at || "").slice(0, 19).replace("T", " ")}</span>
        </div>
      ) : <div className="mt-1 text-[10px] font-mono text-slate-600">NO EVIDENCE</div>}
    </div>
  );

  return (
    <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4 min-w-0" data-testid="assurance-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-50" data-testid="assurance-title">HUSSAR ASSURANCE</h1>
          <p className="text-xs font-mono text-slate-500 mt-1 tracking-widest uppercase">Control Posture — internal control room</p>
          <p className="text-[11px] text-slate-600 mt-1 italic">A control that cannot be evidenced is not a trusted control.</p>
        </div>
        {isOwner && (
        <Button data-testid="run-assurance-cycle-btn" disabled={busy === "cycle"}
                onClick={() => act("cycle", () => api.post("/assurance/cycle", {}), "Assurance cycle complete")}
                className="bg-[#D4AF37] text-black hover:bg-[#C5A059] font-mono text-xs">
          <Play className="w-3 h-3 mr-1" />{busy === "cycle" ? "RUNNING…" : "RUN ASSURANCE CYCLE"}
        </Button>)}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-[#17191E] border border-[#D4AF37]/40 rounded-md p-4" data-testid="readiness-tile">
          <div className="metric-label">Readiness</div>
          <div className="font-mono text-lg font-bold text-[#D4AF37] mt-1" data-testid="readiness-value">
            {posture ? posture.readiness : "—"}
          </div>
        </div>
        <div className="bg-[#17191E] border border-[#2D323E] rounded-md p-4" data-testid="evidence-tile">
          <div className="metric-label">Control Evidence</div>
          <div className="font-mono text-lg font-bold text-slate-50 mt-1" data-testid="evidence-current">
            {posture ? `${posture.control_evidence_current} CURRENT` : "—"}
          </div>
        </div>
        <div className="bg-[#17191E] border border-[#2D323E] rounded-md p-4" data-testid="critical-failures-tile">
          <div className="metric-label">Critical Control Failures</div>
          <div className={`font-mono text-lg font-bold mt-1 ${posture?.critical_control_failures ? "text-red-400" : "text-emerald-400"}`}
               data-testid="critical-failures-value">
            {posture ? posture.critical_control_failures : "—"}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <Tile label="Passing" value={c.passing ?? "—"} testId="tile-passing" tone="text-emerald-400" />
        <Tile label="Failing" value={c.failing ?? "—"} testId="tile-failing" tone={c.failing ? "text-red-400" : "text-slate-50"} />
        <Tile label="Requires Review" value={c.requires_review ?? "—"} testId="tile-review" tone={c.requires_review ? "text-amber-300" : "text-slate-50"} />
        <Tile label="Overdue" value={c.overdue ?? "—"} testId="tile-overdue" tone={c.overdue ? "text-amber-300" : "text-slate-50"} />
        <Tile label="Open Remediation" value={posture?.open_remediations ?? "—"} testId="tile-remediation" />
        <Tile label="Critical Risks" value={posture?.critical_risks ?? "—"} testId="tile-risks" />
        <Tile label="Last Cycle" value={posture?.last_cycle ? String(posture.last_cycle).slice(5, 16).replace("T", " ") : "—"} testId="tile-last-cycle" />
        <Tile label="Reconciliation" value={posture?.reconciliation?.status ?? "—"} testId="tile-reconciliation"
              tone={posture?.reconciliation?.status === "PASS" ? "text-emerald-400" : "text-red-400"} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {keyControl("Audit Integrity", posture?.audit_integrity, "key-audit-integrity")}
        {keyControl("Execution Governance", posture?.execution_governance, "key-execution-governance")}
        {keyControl("Recovery Readiness", posture?.recovery_readiness, "key-recovery-readiness")}
        {keyControl("Latest Evidence", posture?.latest_evidence?.[0], "key-latest-evidence")}
      </div>

      <div className="flex gap-1 flex-wrap border-b border-[#222630] pb-2">
        {TABS.map(([id, label, Icon]) => (
          <button key={id} data-testid={`assurance-tab-${id}`} onClick={() => setTab(id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider rounded-sm border transition-colors ${
                    tab === id ? "bg-[#1E2128] text-[#D4AF37] border-[#D4AF37]/50" : "text-slate-400 border-transparent hover:text-slate-200"}`}>
            <Icon className="w-3 h-3" />{label}
          </button>
        ))}
      </div>

      {tab === "controls" && (
        <Panel title="Control Registry" testId="controls-panel">
          <div className="flex gap-1 flex-wrap mb-3" data-testid="family-filter-row">
            {["ALL", ...Object.keys(families).sort()].map((f) => (
              <button key={f} data-testid={`family-filter-${f}`} onClick={() => setFamFilter(f)}
                      className={`px-2 py-0.5 text-[10px] font-mono rounded-sm border transition-colors ${
                        famFilter === f ? "bg-[#1E2128] text-[#D4AF37] border-[#D4AF37]/50"
                                        : "text-slate-500 border-[#222630] hover:text-slate-300"}`}>
                {f}
              </button>))}
          </div>
          <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-[#222630]">
              <th className="py-2 px-2">Control</th><th className="py-2 px-2">Family</th><th className="py-2 px-2">Severity</th>
              <th className="py-2 px-2">Type</th><th className="py-2 px-2">Status</th><th className="py-2 px-2">SOC 2</th>
              <th className="py-2 px-2">Last Tested</th><th className="py-2 px-2"></th></tr></thead>
            <tbody>
              {(famFilter === "ALL" ? controls
                  : controls.filter((ct) => ct.family === famFilter)).map((ct) => (
                <tr key={ct.control_id} className="border-b border-[#1E2128] hover:bg-[#222630]/40" data-testid={`control-row-${ct.control_id}`}>
                  <td className="py-2 px-2"><div className="font-mono text-[#D4AF37]">{ct.control_id}</div>
                    <div className="text-slate-400 text-[10px]">{ct.title}</div></td>
                  <td className="py-2 px-2 text-slate-400">{families[ct.family] || ct.family}</td>
                  <td className="py-2 px-2"><Badge v={ct.severity} /></td>
                  <td className="py-2 px-2 font-mono text-slate-500">{ct.test_type}</td>
                  <td className="py-2 px-2" data-testid={`control-status-${ct.control_id}`}><Badge v={ct.status} /></td>
                  <td className="py-2 px-2 font-mono text-slate-500 text-[10px]">{(ct.soc2 || []).join(", ")}</td>
                  <td className="py-2 px-2 font-mono text-slate-500 text-[10px]">{String(ct.last_tested_at || "—").slice(0, 16).replace("T", " ")}</td>
                  <td className="py-2 px-2 text-right">
                    {ct.test_type === "automated" && (
                      <Button size="sm" variant="outline" data-testid={`retest-btn-${ct.control_id}`}
                              disabled={busy === `t-${ct.control_id}`}
                              onClick={() => act(`t-${ct.control_id}`,
                                () => api.post(`/assurance/controls/${ct.control_id}/test`, {}),
                                `${ct.control_id} retested`)}
                              className="h-6 text-[10px] font-mono border-[#2D323E]">RETEST</Button>)}
                  </td>
                </tr>))}
            </tbody>
          </table>
          </div>
        </Panel>
      )}

      {tab === "evidence" && (
        <Panel title="Evidence Vault" testId="evidence-panel">
          <div className="text-[10px] font-mono text-slate-500 mb-3" data-testid="evidence-immutability-note">
            Insert-only, hash-sealed, audit-chain-linked — a later PASS never overwrites a historical FAIL.
            {" "}{evidence.length} records retained under COMPLIANCE_7Y.
          </div>
          <select data-testid="evidence-control-filter" value={evControl} onChange={(e) => setEvControl(e.target.value)}
                  className="bg-[#0B0C0E] border border-[#2D323E] rounded-sm px-2 py-1 text-xs text-slate-200 mb-3 block">
            <option value="">ALL CONTROLS</option>
            {[...new Set(evidence.map((e) => e.control_id))].sort().map((cid) => (
              <option key={cid} value={cid}>{cid}</option>))}
          </select>
          <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-[#222630]">
              <th className="py-2 px-2">Evidence</th><th className="py-2 px-2">Control</th>
              <th className="py-2 px-2">Result</th><th className="py-2 px-2">Generated</th>
              <th className="py-2 px-2">Source</th><th className="py-2 px-2">Seal</th>
              <th className="py-2 px-2">By</th><th className="py-2 px-2"></th></tr></thead>
            <tbody>
              {evidence.filter((e) => !evControl || e.control_id === evControl).map((e) => (
                <tr key={e.evidence_id} className="border-b border-[#1E2128] hover:bg-[#222630]/40" data-testid={`evidence-row-${e.evidence_id}`}>
                  <td className="py-2 px-2 font-mono text-[#D4AF37]">{e.evidence_id}</td>
                  <td className="py-2 px-2 font-mono text-slate-400">{e.control_id}</td>
                  <td className="py-2 px-2" data-testid={`evidence-result-${e.evidence_id}`}><Badge v={e.result} /></td>
                  <td className="py-2 px-2 font-mono text-slate-500 text-[10px]">{String(e.generated_at).slice(0, 19).replace("T", " ")}</td>
                  <td className="py-2 px-2 font-mono text-slate-500 text-[10px]">{e.evidence_source}</td>
                  <td className="py-2 px-2 font-mono text-slate-500 text-[10px]" title={e.evidence_hash}>{String(e.evidence_hash).slice(0, 12)}…</td>
                  <td className="py-2 px-2 font-mono text-slate-500 text-[10px]">{e.created_by}</td>
                  <td className="py-2 px-2 text-right">
                    <Button size="sm" variant="outline" data-testid={`verify-evidence-${e.evidence_id}`}
                            disabled={busy === `ev-${e.evidence_id}`}
                            onClick={() => act(`ev-${e.evidence_id}`, async () => {
                              const r = await api.get(`/assurance/evidence/${e.evidence_id}/verify`);
                              if (!r.data.valid) throw new Error(r.data.reason || "Evidence seal INVALID");
                            }, `Evidence ${e.evidence_id} seal VALID`)}
                            className="h-6 text-[10px] font-mono border-[#2D323E]">VERIFY</Button>
                  </td>
                </tr>))}
            </tbody>
          </table>
          </div>
        </Panel>
      )}

      {tab === "remediation" && (
        <Panel title="Remediation Queue" testId="remediation-panel">
          {rems.length === 0 && <div className="text-xs font-mono text-slate-500 py-4" data-testid="remediation-empty">NO OPEN REMEDIATION — all controls restored or proven.</div>}
          {rems.map((r) => (
            <div key={r.remediation_id} className="border border-[#222630] rounded-md p-3 mb-2 bg-[#121316]" data-testid={`remediation-${r.remediation_id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[#D4AF37] text-xs">{r.remediation_id}</span>
                <span className="font-mono text-slate-300 text-xs">{r.control_id}</span>
                <Badge v={r.severity} /><Badge v={r.status} />
                <span className="text-[10px] font-mono text-slate-500 ml-auto">due {String(r.due_at).slice(0, 10)}</span>
              </div>
              <div className="flex gap-1 mt-2 flex-wrap">
                {{"OPEN": "ACKNOWLEDGED", "ACKNOWLEDGED": "IN_REMEDIATION", "IN_REMEDIATION": "READY_FOR_RETEST",
                  "READY_FOR_RETEST": "VERIFIED", "VERIFIED": "CLOSED"}[r.status] && (
                  <Button size="sm" variant="outline" data-testid={`remediation-advance-${r.remediation_id}`}
                          onClick={() => transition("remediations", r.remediation_id,
                            {"OPEN": "ACKNOWLEDGED", "ACKNOWLEDGED": "IN_REMEDIATION",
                             "IN_REMEDIATION": "READY_FOR_RETEST", "READY_FOR_RETEST": "VERIFIED",
                             "VERIFIED": "CLOSED"}[r.status])}
                          className="h-6 text-[10px] font-mono border-[#2D323E]">
                    → {{"OPEN": "ACKNOWLEDGED", "ACKNOWLEDGED": "IN_REMEDIATION", "IN_REMEDIATION": "READY_FOR_RETEST",
                        "READY_FOR_RETEST": "VERIFIED", "VERIFIED": "CLOSED"}[r.status]}
                  </Button>)}
              </div>
            </div>))}
        </Panel>
      )}

      {tab === "policies" && (
        <Panel title="Policy Center" testId="policies-panel">
          {policies.map((p) => (
            <div key={p.id} className="flex items-center gap-2 border-b border-[#1E2128] py-2" data-testid={`policy-${p.key}-v${p.version}`}>
              <FileCheck className="w-3 h-3 text-slate-500" />
              <span className="text-xs text-slate-200">{p.title}</span>
              <span className="text-[10px] font-mono text-slate-500">v{p.version}</span>
              <Badge v={p.status} />
              <span className="ml-auto flex gap-1">
                {p.status === "DRAFT" && (
                  <Button size="sm" variant="outline" data-testid={`policy-approve-${p.key}-v${p.version}`}
                          onClick={() => transition("policies", p.key, "OWNER_APPROVED", { version: p.version })}
                          className="h-6 text-[10px] font-mono border-[#2D323E]">OWNER APPROVE</Button>)}
                {p.status === "OWNER_APPROVED" && (
                  <Button size="sm" variant="outline" data-testid={`policy-effective-${p.key}-v${p.version}`}
                          onClick={() => transition("policies", p.key, "EFFECTIVE", { version: p.version })}
                          className="h-6 text-[10px] font-mono border-[#D4AF37]/50 text-[#D4AF37]">MAKE EFFECTIVE</Button>)}
              </span>
            </div>))}
        </Panel>
      )}

      {tab === "access" && (
        <Panel title="Access Reviews" testId="access-panel"
               right={<Button size="sm" data-testid="generate-access-review-btn"
                                onClick={() => act("acr", () => api.post("/assurance/access-reviews/generate", { scope: "FULL" }), "Access review generated")}
                                className="h-7 text-[10px] font-mono bg-[#D4AF37] text-black hover:bg-[#C5A059]">GENERATE REVIEW</Button>}>
          {reviews.map((r) => (
            <div key={r.review_id} className="border border-[#222630] rounded-md p-3 mb-2 bg-[#121316]" data-testid={`access-review-${r.review_id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[#D4AF37] text-xs">{r.review_id}</span>
                <Badge v={r.status} />
                <span className="text-[10px] font-mono text-slate-500">{r.scope} · {r.snapshot.users.length} users · {r.snapshot.accounts.length} accounts · {r.snapshot.provider_credentials.length} credential sets</span>
                {r.status === "OPEN" && (
                  <Button size="sm" variant="outline" data-testid={`attest-${r.review_id}`}
                          onClick={() => act(`att-${r.review_id}`,
                            () => api.post(`/assurance/access-reviews/${r.review_id}/attest`,
                                           { approved: true, findings: "Reviewed via Assurance UI." }),
                            "Review attested")}
                          className="h-6 text-[10px] font-mono border-[#2D323E] ml-auto">ATTEST — ACCESS APPROPRIATE</Button>)}
              </div>
            </div>))}
        </Panel>
      )}

      {tab === "vendors" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Panel title="Vendor Register" testId="vendors-panel">
            {vendors.map((v) => (
              <div key={v.id} className="border-b border-[#1E2128] py-2" data-testid={`vendor-${v.id}`}>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-200">{v.name}</span>
                  <Badge v={v.risk_tier} />
                  <Button size="sm" variant="outline" data-testid={`vendor-review-${v.id}`}
                          onClick={() => act(`vr-${v.id}`, () => api.post(`/assurance/vendors/${v.id}/review`, { notes: "Reviewed via Assurance UI." }), `${v.name} reviewed`)}
                          className="h-6 text-[10px] font-mono border-[#2D323E] ml-auto">MARK REVIEWED</Button>
                </div>
                <div className="text-[10px] font-mono text-slate-500 mt-0.5">{v.category} · {v.data_access}</div>
              </div>))}
          </Panel>
          <Panel title="Risk Register" testId="risks-panel">
            {risks.map((r) => (
              <div key={r.id} className="border-b border-[#1E2128] py-2" data-testid={`risk-${r.id}`}>
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-3 h-3 text-amber-300" />
                  <span className="text-xs text-slate-200">{r.title}</span>
                  <Badge v={r.status} />
                  <span className="text-[10px] font-mono text-slate-500 ml-auto">{r.inherent_risk}→{r.residual_risk}</span>
                </div>
                <div className="text-[10px] font-mono text-slate-500 mt-0.5">controls: {(r.mitigating_controls || []).join(", ")}</div>
              </div>))}
          </Panel>
        </div>
      )}

      {tab === "incidents" && (
        <Panel title="Incident Center" testId="incidents-panel"
               right={<Button size="sm" data-testid="create-incident-btn"
                                onClick={() => {
                                  if (!incidentForm.title.trim()) { toast.error("Title required"); return; }
                                  act("inc", () => api.post("/assurance/incidents", incidentForm), "Incident recorded");
                                  setIncidentForm({ title: "", severity: "MEDIUM", description: "" });
                                }}
                                className="h-7 text-[10px] font-mono bg-[#D4AF37] text-black hover:bg-[#C5A059]">RECORD INCIDENT</Button>}>
          <div className="flex gap-2 mb-3 flex-wrap">
            <input data-testid="incident-title-input" value={incidentForm.title}
                   onChange={(e) => setIncidentForm({ ...incidentForm, title: e.target.value })}
                   placeholder="Incident title"
                   className="bg-[#0B0C0E] border border-[#2D323E] rounded-sm px-2 py-1 text-xs text-slate-200 flex-1 min-w-[200px]" />
            <input data-testid="incident-description-input" value={incidentForm.description}
                   onChange={(e) => setIncidentForm({ ...incidentForm, description: e.target.value })}
                   placeholder="Description"
                   className="bg-[#0B0C0E] border border-[#2D323E] rounded-sm px-2 py-1 text-xs text-slate-200 flex-1 min-w-[200px]" />
          </div>
          {incidents.map((i) => (
            <div key={i.incident_id} className="border border-[#222630] rounded-md p-3 mb-2 bg-[#121316]" data-testid={`incident-${i.incident_id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[#D4AF37] text-xs">{i.incident_id}</span>
                <span className="text-xs text-slate-200">{i.title}</span>
                <Badge v={i.severity} /><Badge v={i.status} />
                {i.status !== "CLOSED" && (
                  <Button size="sm" variant="outline" data-testid={`incident-advance-${i.incident_id}`}
                          onClick={() => {
                            const next = {"DETECTED": "TRIAGED", "TRIAGED": "CONTAINED", "CONTAINED": "REMEDIATED",
                                          "REMEDIATED": "VERIFIED", "VERIFIED": "CLOSED"}[i.status];
                            const extra = next === "CLOSED" ? { regression_test_created: "NOT_REQUIRED" } : {};
                            transition("incidents", i.incident_id, next, extra);
                          }}
                          className="h-6 text-[10px] font-mono border-[#2D323E] ml-auto">
                    → {{"DETECTED": "TRIAGED", "TRIAGED": "CONTAINED", "CONTAINED": "REMEDIATED",
                        "REMEDIATED": "VERIFIED", "VERIFIED": "CLOSED"}[i.status]}
                  </Button>)}
              </div>
              {i.regression_test_created && (
                <div className="text-[10px] font-mono text-slate-500 mt-1">
                  regression test: {i.regression_test_created}{i.regression_test_ref ? ` — ${i.regression_test_ref}` : ""}</div>)}
            </div>))}
        </Panel>
      )}

      {tab === "soc2" && soc2 && (
        <Panel title="SOC 2 Trust Services Criteria Mapping" testId="soc2-panel">
          <div className="text-[10px] font-mono text-amber-300/80 border border-amber-800/40 rounded-sm p-2 mb-3" data-testid="soc2-disclaimer">
            {soc2.disclaimer}
          </div>
          <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-[#222630]">
              <th className="py-2 px-2">Hussar Control</th><th className="py-2 px-2">Family</th>
              <th className="py-2 px-2">Status</th><th className="py-2 px-2">SOC 2 Criteria</th></tr></thead>
            <tbody>
              {soc2.mapping.map((m) => (
                <tr key={m.control_id} className="border-b border-[#1E2128]" data-testid={`soc2-row-${m.control_id}`}>
                  <td className="py-2 px-2"><span className="font-mono text-[#D4AF37]">{m.control_id}</span>
                    <span className="text-slate-400 text-[10px] ml-2">{m.title}</span></td>
                  <td className="py-2 px-2 text-slate-400">{m.family}</td>
                  <td className="py-2 px-2"><Badge v={m.status} /></td>
                  <td className="py-2 px-2 font-mono text-slate-300">{m.soc2.join(", ")}</td>
                </tr>))}
            </tbody>
          </table>
          </div>
        </Panel>
      )}

      <div className="text-[10px] font-mono text-slate-600 border-t border-[#222630] pt-3 flex items-center gap-2" data-testid="assurance-doctrine">
        <ShieldAlert className="w-3 h-3 text-[#D4AF37]" />
        Assurance observes and proves governance. It does not grant itself authority over governance.
      </div>
    </div>
  );
}
